import { createContext, useContext, useMemo } from 'react';
import { ChildrenProps } from '@dfl/mui-react-common';
import { useFindOneCollections } from '../hooks/useFindOneCollections';
import { ICollection } from '../interfaces';
import { useParams } from 'react-router';
import { COLLECTION_CONTENT_TYPE } from '../constants/collection-types';
// Data value of the provider context
type CollectionContextValue = {
  collection?: ICollection;
  parentCollection?: ICollection;
  isLoading?: boolean;
  error?: any;
  collectionId?: string;
  activeCollectionId?: string;
  contentType?: COLLECTION_CONTENT_TYPE;
  isRegionVariant?: boolean;
};
// default value of the context
const defaultValue: CollectionContextValue = {};

// create context
const CollectionContext = createContext<CollectionContextValue>(defaultValue);

// Proptypes of Provider component
type CollectionContextProps = ChildrenProps & {
  children: any;
  contentType: COLLECTION_CONTENT_TYPE;
};

/**
 * Provider component
 * */
const CollectionProvider = ({ contentType, ...props }: CollectionContextProps) => {
  const { id, regionId } = useParams();
  const collectionId: string = id as string;

  const activeCollectionId = useMemo(() => {
    if (!regionId || regionId === 'all') {
      return collectionId;
    }
    return regionId;
  }, [regionId, collectionId]);

  const isRegionVariant = useMemo(() => {
    return Boolean(regionId && regionId !== 'all');
  }, [regionId]);

  const { data: parentData, isLoading: parentLoading } = useFindOneCollections(
    collectionId,
    contentType,
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: activeData, isLoading: activeLoading, error } = useFindOneCollections(
    activeCollectionId,
    contentType,
    {
      enabled: !!activeCollectionId && isRegionVariant,
      staleTime: 5 * 60 * 1000
    }
  );

  const collection = useMemo(() => {
    if (isRegionVariant && parentData && activeData) {
      return {
        ...activeData,
        regions: parentData.regions,
      };
    }
    return parentData;
  }, [isRegionVariant, parentData, activeData]);

  const isLoading = parentLoading || activeLoading;

  return (
    <CollectionContext.Provider
      value={{
        collection,
        parentCollection: parentData,
        isLoading,
        error,
        collectionId,
        activeCollectionId,
        contentType,
        isRegionVariant,
      }}
      {...props}
    />
  );
};

// Default hook to retrieve context data
const useCollectionDetails = () => {
  const context = useContext(CollectionContext);
  if (context === undefined) {
    return {}; // also, you can throw an error if it is you need the context
  }
  return context;
};

export { CollectionProvider, useCollectionDetails };
